import { useEffect, useRef, useState, useCallback } from 'react';

export interface ScrollAnimationOptions {
    threshold?: number;
    rootMargin?: string;
    triggerOnce?: boolean;
    delay?: number;
    stagger?: number;
}

export interface AnimationVariant {
    initial: React.CSSProperties;
    animate: React.CSSProperties;
    transition?: string;
}

export const animationVariants = {
    fadeInUp: {
        initial: {
            opacity: 0,
            transform: 'translateY(50px)',
        },
        animate: {
            opacity: 1,
            transform: 'translateY(0)',
        },
        transition: 'all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
    },
    fadeInDown: {
        initial: {
            opacity: 0,
            transform: 'translateY(-50px)',
        },
        animate: {
            opacity: 1,
            transform: 'translateY(0)',
        },
        transition: 'all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
    },
    fadeInLeft: {
        initial: {
            opacity: 0,
            transform: 'translateX(-50px)',
        },
        animate: {
            opacity: 1,
            transform: 'translateX(0)',
        },
        transition: 'all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
    },
    fadeInRight: {
        initial: {
            opacity: 0,
            transform: 'translateX(50px)',
        },
        animate: {
            opacity: 1,
            transform: 'translateX(0)',
        },
        transition: 'all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
    },
    scaleIn: {
        initial: {
            opacity: 0,
            transform: 'scale(0.8)',
        },
        animate: {
            opacity: 1,
            transform: 'scale(1)',
        },
        transition: 'all 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
    },
    rotateIn: {
        initial: {
            opacity: 0,
            transform: 'rotateY(-15deg) translateY(50px)',
        },
        animate: {
            opacity: 1,
            transform: 'rotateY(0deg) translateY(0)',
        },
        transition: 'all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
    },
    slideInUp: {
        initial: {
            opacity: 0,
            transform: 'translateY(100px)',
        },
        animate: {
            opacity: 1,
            transform: 'translateY(0)',
        },
        transition: 'all 1s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
    },
    bounceIn: {
        initial: {
            opacity: 0,
            transform: 'scale(0.3)',
        },
        animate: {
            opacity: 1,
            transform: 'scale(1)',
        },
        transition: 'all 0.6s cubic-bezier(0.68, -0.55, 0.265, 1.55)',
    },
} as const;

export type AnimationVariantName = keyof typeof animationVariants;

/**
 * 通用的滚动动画Hook
 * @param variant 动画变体名称或自定义动画配置
 * @param options 动画选项
 * @returns 动画状态和ref
 */
export function useScrollAnimation(variant: AnimationVariantName | AnimationVariant = 'fadeInUp', options: ScrollAnimationOptions = {}) {
    const { threshold = 0.1, rootMargin = '0px 0px -50px 0px', triggerOnce = true, delay = 0 } = options;

    const ref = useRef<HTMLElement>(null);
    const [isVisible, setIsVisible] = useState(false);
    const [hasTriggered, setHasTriggered] = useState(false);

    const animationConfig = typeof variant === 'string' ? animationVariants[variant] : variant;

    useEffect(() => {
        const element = ref.current;
        if (!element) return;

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setTimeout(() => {
                        setIsVisible(true);
                        if (triggerOnce) {
                            setHasTriggered(true);
                        }
                    }, delay);

                    if (triggerOnce) {
                        observer.unobserve(element);
                    }
                } else if (!triggerOnce && !hasTriggered) {
                    setIsVisible(false);
                }
            },
            {
                threshold,
                rootMargin,
            },
        );

        observer.observe(element);

        return () => {
            observer.disconnect();
        };
    }, [threshold, rootMargin, triggerOnce, delay, hasTriggered]);

    const animationStyle: React.CSSProperties = {
        ...animationConfig.initial,
        transition: animationConfig.transition,
        ...(isVisible ? animationConfig.animate : {}),
    };

    return {
        ref,
        isVisible,
        animationStyle,
        reset: () => {
            setIsVisible(false);
            setHasTriggered(false);
        },
    };
}

/**
 * 用于多个元素的交错动画Hook
 * @param variant 动画变体名称
 * @param count 元素数量
 * @param options 动画选项
 * @returns 动画状态数组和refs数组
 */
export function useStaggeredScrollAnimation(
    variant: AnimationVariantName | AnimationVariant = 'fadeInUp',
    count: number,
    options: ScrollAnimationOptions = {},
) {
    const { stagger = 0.2, ...restOptions } = options;
    const refs = useRef<(HTMLElement | null)[]>([]);
    const [visibilityStates, setVisibilityStates] = useState<boolean[]>(new Array(count).fill(false));

    const animationConfig = typeof variant === 'string' ? animationVariants[variant] : variant;

    useEffect(() => {
        refs.current = refs.current.slice(0, count);
        setVisibilityStates(new Array(count).fill(false));
    }, [count]);

    const createRef = useCallback((index: number) => {
        return (element: HTMLElement | null) => {
            refs.current[index] = element;
        };
    }, []);

    useEffect(() => {
        const observers: IntersectionObserver[] = [];

        // 使用定时器来确保所有ref都已经设置
        const timer = setTimeout(() => {
            refs.current.forEach((element, index) => {
                if (!element) return;

                const observer = new IntersectionObserver(
                    ([entry]) => {
                        if (entry.isIntersecting) {
                            setTimeout(() => {
                                setVisibilityStates((prev) => {
                                    const newStates = [...prev];
                                    newStates[index] = true;
                                    return newStates;
                                });
                            }, (restOptions.delay || 0) + index * stagger * 1000);

                            if (restOptions.triggerOnce !== false) {
                                observer.unobserve(element);
                            }
                        }
                    },
                    {
                        threshold: restOptions.threshold || 0.1,
                        rootMargin: restOptions.rootMargin || '0px 0px -50px 0px',
                    },
                );

                observer.observe(element);
                observers.push(observer);
            });
        }, 100); // 给一点时间让refs设置完成

        return () => {
            clearTimeout(timer);
            observers.forEach((observer) => observer.disconnect());
        };
    }, [count, stagger, restOptions.delay, restOptions.threshold, restOptions.rootMargin, restOptions.triggerOnce]);

    const getAnimationStyle = (index: number): React.CSSProperties => ({
        ...animationConfig.initial,
        transition: animationConfig.transition,
        ...(visibilityStates[index] ? animationConfig.animate : {}),
    });

    return {
        refs: refs.current,
        createRef,
        visibilityStates,
        getAnimationStyle,
        reset: () => setVisibilityStates(new Array(count).fill(false)),
    };
}

/**
 * 防抖Hook
 */
export function useThrottle<T extends (...args: any[]) => any>(callback: T, delay: number): T {
    const throttleRef = useRef<NodeJS.Timeout | null>(null);
    const lastCallRef = useRef<number>(0);

    return useCallback(
        ((...args) => {
            const now = Date.now();

            if (now - lastCallRef.current >= delay) {
                lastCallRef.current = now;
                callback(...args);
            } else {
                if (throttleRef.current) {
                    clearTimeout(throttleRef.current);
                }

                throttleRef.current = setTimeout(() => {
                    lastCallRef.current = Date.now();
                    callback(...args);
                }, delay - (now - lastCallRef.current));
            }
        }) as T,
        [callback, delay],
    );
}
